const DB_NAME = 'liftdb'
const DB_VERSION = 2;
let DB;

export default {
  async getDb() {
    return new Promise((resolve, reject) => {
      if (DB) {
        return resolve(DB);
      }
      let request = window.indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        reject("Error");
      };

      request.onsuccess = e => {
        DB = e.target.result;
        resolve(DB);
      };

      request.onupgradeneeded = e => {
        let db = e.target.result;

        // get list of existing stores
        let stores = db.objectStoreNames;
        this.checkAndCreateStore(stores, "technicalInfo", "facility", db)
        this.checkAndCreateStore(stores, "facilityState", "facility", db)
        this.checkAndCreateStore(stores, "processResult", "id", db)
        this.checkAndCreateStore(stores, "processStepsAndResults", "id", db)
      }
    })
  },

  /**
   * Checks if the store already exists, if not, it will be added do the indexedDB
   *
   * @param existingStores list of existing stores
   * @param storeToCreate store to check resp. create
   * @param storeKey key of store when it should be added
   * @param db db to add new stores to it
   */
  checkAndCreateStore(existingStores, storeToCreate, storeKey, db) {
    if (!existingStores.contains(storeToCreate)) {
      db.createObjectStore(storeToCreate, { keyPath: storeKey});
    }
  },

  /* DB methods for technical info */
  async deleteTechnicalInfo(technicalInfo) {
    let db = await this.getDb();

    return new Promise(resolve => {
      let trans = db.transaction(['technicalInfo'], 'readwrite');
      trans.oncomplete = () => {
        resolve();
      };

      let store = trans.objectStore('technicalInfo');
      store.delete(technicalInfo.facility);
    });
  },

  async readTechnicalInfo(technicalInfo) {
    let db = await this.getDb();

    return new Promise(resolve => {
      let trans = db.transaction(['technicalInfo'], 'readonly');
      trans.oncomplete = () => {
        resolve(result);
      };

      let store = trans.objectStore('technicalInfo');
      let result;

      store.get(technicalInfo.facility).onsuccess = e => {
        result = e.target.result;
      }
    });
  },

  async saveTechnicalInfo(technicalInfo) {
    let db = await this.getDb();

    return new Promise(resolve => {
      let trans = db.transaction(['technicalInfo'], 'readwrite');
      trans.oncomplete = () => {
        resolve();
      };

      let store = trans.objectStore('technicalInfo');
      store.put(technicalInfo);

    });
  },


  /* DB methods for facility state */
  async deleteFacilityState(facilityState) {
    let db = await this.getDb();

    return new Promise(resolve => {
      let trans = db.transaction(['facilityState'], 'readwrite');
      trans.oncomplete = () => {
        resolve();
      };

      let store = trans.objectStore('facilityState');
      store.delete(facilityState.facility);
    });
  },

  async readFacilityState(facilityState) {
    let db = await this.getDb();

    return new Promise(resolve => {
      let trans = db.transaction(['facilityState'], 'readonly');
      trans.oncomplete = () => {
        resolve(result);
      };

      let store = trans.objectStore('facilityState');
      let result;

      store.get(facilityState.facility).onsuccess = e => {
        result = e.target.result;
      }
    });
  },

  async saveFacilityState(facilityState) {
    let db = await this.getDb();

    return new Promise(resolve => {
      let trans = db.transaction(['facilityState'], 'readwrite');
      trans.oncomplete = () => {
        resolve();
      };

      let store = trans.objectStore('facilityState');
      store.put(facilityState);

    });
  },

  /* Generic DB methods */

  /**
   * Delete specific object from store
   *
   * @param key key of object e.g. '679f7f77-e796-4ca0-b09b-16e0f46359b3'
   * @param objectStore store to delete object out of e.g. 'processResult'
   * @returns {Promise<unknown>}
   */
  async delete(key, objectStore) {
    let db = await this.getDb();

    return new Promise(resolve => {
      let transaction = db.transaction([objectStore], 'readwrite');
      transaction.oncomplete = () => {
        resolve();
      }

      transaction.objectStore(objectStore).delete(key);
    })
  },

  /**
   * Removes all entries of a object store
   *
   * @param objectStore store to clear e.g. 'processResult'
   * @returns {Promise<unknown>}
   */
  async clearObjectStore(objectStore) {
    let db = await this.getDb();

    return new Promise(resolve => {
      let transaction = db.transaction([objectStore], 'readwrite');
      transaction.oncomplete = () => {
        resolve();
      }

      transaction.objectStore(objectStore).clear();
    })
  },

  /**
   * Read specific object from store
   *
   * @param key key of object to get e.g. '679f7f77-e796-4ca0-b09b-16e0f46359b3'
   * @param objectStore store to read object out of e.g. 'processResult'
   * @returns {Promise<unknown>}
   */
  async read(key, objectStore) {
    let db = await this.getDb();

    return new Promise(resolve => {
      let transaction = db.transaction([objectStore], 'readonly');
      transaction.oncomplete = () => {
        resolve(result);
      }

      let store = transaction.objectStore(objectStore);
      let result;

      store.get(key).onsuccess = e => {
        result = e.target.result;
      }
    })
  },

  /**
   * Read all entries from a store
   *
   * @param objectStore store to read all entries out of e.g. 'processResult'
   * @returns {Promise<unknown>}
   */
  async readAll(objectStore) {
    let db = await this.getDb();

    return new Promise(resolve => {
      let transaction = db.transaction([objectStore], 'readonly');
      transaction.oncomplete = () => {
        resolve(result);
      }

      let store = transaction.objectStore(objectStore);
      let result;

      store.getAll().onsuccess = e => {
        result = e.target.result;
      }
    })
  },

  /**
   * Save a new object or update an existing object of a store
   * Info: If a object with the same identifier is already existing in the object store
   * indexedDB automatically updates the existing object with the passed one
   *
   * @param object object to add to store
   * @param objectStore store where new object should be added e.g. 'processResult'
   * @returns {Promise<unknown>}
   */
  async save(object, objectStore) {
    let db = await this.getDb();

    return new Promise(resolve => {
      let transaction = db.transaction([objectStore], 'readwrite');
      transaction.oncomplete = () => {
        resolve();
      }

      transaction.objectStore(objectStore).put(object);
    })
  }
}